import { IBrowzwearBoard, IBrowzwearFolder } from "api/browzwear/browzwear.type";

/**
 * The Browzwear tree, as the UI needs it (REF-6498).
 *
 * Pure functions on purpose — the page they serve pulls the store, the router and `Search`, so the
 * only way to test the three rules that actually caused bugs is to keep them out of the component
 * (same constraint `browzwearPickerItems.ts` documents).
 *
 * The three rules:
 *
 *  • **A Collection is a GROUP whose `type` is `collection`.** There is no collection endpoint and
 *    no collection object. `/import` returns it as `folders[].group_type`; measured on a live store,
 *    92 groups split `guests` 66 / `collection` 13 / `members` 12 / `public` 1. Every consumer here
 *    used to do `folders.flatMap(f => f.boards)`, which throws the group away entirely — so the 13
 *    collections were unreachable and the user's report was, literally, "I can see Folders, I cannot
 *    see Collections".
 *  • **A virtual board is not an export target.** A group with no boards is returned with a
 *    synthesised board whose id is the GROUP id (`is_virtual: true`). Listing its styles works, but
 *    `POST /styles/` takes a real board as `parent_id`.
 *  • **Nothing is paginated server-side yet**, so the grids page in memory. The whole tree arrives
 *    in one payload; a store with 128 boards and 1146 images rendered every card and every `<img>`
 *    at once.
 */

export const COLLECTION_GROUP_TYPE = "collection";

/** Cards per page in the folder / image grids. */
export const PAGE_SIZE = 24;

export const isCollection = (folder: IBrowzwearFolder): boolean => folder.group_type === COLLECTION_GROUP_TYPE;

/** Collections first, then everything else, each in the order the API returned them. */
export const sortCollectionsFirst = (folders: IBrowzwearFolder[]): IBrowzwearFolder[] => [...folders.filter(isCollection), ...folders.filter((folder) => !isCollection(folder))];

/**
 * Filter on the folder's own name OR any board name inside it — a user searching for a board they
 * remember should not have to know which collection holds it.
 */
export const filterFolders = (folders: IBrowzwearFolder[], query: string): IBrowzwearFolder[] => {
  const needle = query.trim().toLowerCase();
  if (!needle) return folders;

  return folders.filter((folder) => (folder.group_name ?? "").toLowerCase().includes(needle) || (folder.boards ?? []).some((board) => (board.board_name ?? "").toLowerCase().includes(needle)));
};

/** Every image in a folder, for the folder card's 4 thumbnails and its count. */
export const folderImageUrls = (folder: IBrowzwearFolder): string[] =>
  (folder.boards ?? []).flatMap((board) => (board.styles ?? []).flatMap((style) => (style.images ?? []).map((image) => image.image_url)));

export const boardImageUrls = (board: IBrowzwearBoard): string[] => (board.styles ?? []).flatMap((style) => (style.images ?? []).map((image) => image.image_url));

/**
 * Boards the export modal may offer. A `is_virtual` board's id is a group id, and an export aimed at
 * one has nowhere valid to land — Stylezone answers `403 Permission denied` and, before REF-6498,
 * the BE reported that as a 200.
 */
export const exportableBoards = (boards: IBrowzwearBoard[]): IBrowzwearBoard[] => boards.filter((board) => !board.is_virtual);

export const pageCount = (total: number, pageSize: number = PAGE_SIZE): number => Math.max(1, Math.ceil(total / pageSize));

/** 1-indexed, and clamped: a page that no longer exists (the search narrowed) returns the last one. */
export const pageOf = <T>(items: T[], page: number, pageSize: number = PAGE_SIZE): T[] => {
  const last = pageCount(items.length, pageSize);
  const safe = Math.min(Math.max(1, page), last);

  return items.slice((safe - 1) * pageSize, safe * pageSize);
};
